import { useEffect, useRef, useState, useCallback } from "react";
import { WS_URL } from "@/lib/api";
import { debugLog } from "@/lib/debugFlags";

/**
 * Manages a single WebSocket connection with auto-reconnect backoff.
 *
 * Contract:
 *   - AT MOST ONE live socket at a time. Any prior socket is closed
 *     (and its handlers detached) before a new one is created.
 *   - Reconnects are exponentially backed off and cancellable — if the
 *     component unmounts mid-reconnect the pending timer is cleared and
 *     no new socket is spawned.
 *   - `onEvent(evt)` is called on every non-hello/pong frame. The caller's
 *     latest reference is used (see `onEventRef`) so state closures stay fresh.
 *
 * Phase 7 additions:
 *   - Emits `{type:"presence", state:"active"|"idle"}` on visibility /
 *     focus / blur so the backend can dedupe OS push against the in-app
 *     WS chime (see `ws.py::ConnectionManager.is_user_active`).
 *   - The 30 s ping doubles as a presence refresh so a stale entry never
 *     lingers past ~45 s on the backend.
 *
 * Phase 8.1 hardening (bug fix Jul-20-2026): a `?debug=ws` URL flag prints
 * every socket lifecycle event, and an `epoch` counter fences out orphaned
 * onclose handlers left over from React StrictMode dev double-invocations
 * and reconnect races (root cause of the duplicated toasts we saw in
 * Playwright: two live sockets both delivering the same `message.new`).
 */
export function useWebSocket(onEvent, enabled = true) {
    const wsRef = useRef(null);
    const reconnectTimer = useRef(null);
    const retriesRef = useRef(0);
    const onEventRef = useRef(onEvent);
    onEventRef.current = onEvent;

    // Monotonic id — every time the hook (re-)mounts the effect body we bump
    // this. Handlers on OLD sockets check they still match the current epoch
    // before running any side-effect (reconnect scheduling, state setting).
    // This makes React StrictMode double-invocation safe: the first socket's
    // onclose that fires AFTER the second socket has already been created
    // simply sees a stale epoch and no-ops.
    const epochRef = useRef(0);
    const teardownRef = useRef(null);

    const [status, setStatus] = useState("disconnected"); // disconnected | connecting | connected

    const sendJson = useCallback((obj) => {
        try {
            if (wsRef.current && wsRef.current.readyState === 1) {
                wsRef.current.send(JSON.stringify(obj));
            }
        } catch (_) { /* ignore */ }
    }, []);

    const isTabActive = () =>
        typeof document !== "undefined"
        && document.visibilityState === "visible"
        && document.hasFocus();

    const connect = useCallback(() => {
        if (!enabled) return;
        // Guard: if we somehow already have an open socket, close it first
        // to preserve the "at most one live socket" invariant.
        if (wsRef.current && wsRef.current.readyState <= 1) {
            try { wsRef.current.close(); } catch (_) { /* already closed */ }
            wsRef.current = null;
        }
        const epoch = epochRef.current;
        setStatus("connecting");
        debugLog("ws", `connect() epoch=${epoch} url=${WS_URL}`);
        try {
            const ws = new WebSocket(WS_URL);
            wsRef.current = ws;
            ws.onopen = () => {
                if (epoch !== epochRef.current) {
                    debugLog("ws", `onopen ignored (stale epoch=${epoch}, current=${epochRef.current})`);
                    try { ws.close(); } catch (_) { /* ignore */ }
                    return;
                }
                retriesRef.current = 0;
                setStatus("connected");
                debugLog("ws", `onopen epoch=${epoch}`);
                // Emit our current presence immediately so a freshly-opened
                // tab isn't misclassified as idle for the first 30 s.
                sendJson({ type: "presence", state: isTabActive() ? "active" : "idle" });
            };
            ws.onmessage = (evt) => {
                if (epoch !== epochRef.current) return;  // orphan socket
                try {
                    const data = JSON.parse(evt.data);
                    if (data.type === "hello" || data.type === "pong") return;
                    debugLog("notif", "WS msg received:", data.type,
                             data.type === "message.new" ? { conv: data.conversation_id, dir: data.message?.direction, sender: data.message?.sender_name } : null);
                    onEventRef.current?.(data);
                } catch (_) { /* ignore */ }
            };
            ws.onclose = (ev) => {
                if (epoch !== epochRef.current) {
                    debugLog("ws", `onclose ignored (stale epoch=${epoch})`);
                    return;
                }
                setStatus("disconnected");
                debugLog("ws", `onclose epoch=${epoch} code=${ev.code} clean=${ev.wasClean}`);
                if (!enabled) return;
                retriesRef.current += 1;
                const delay = Math.min(15000, 1000 * Math.pow(2, retriesRef.current));
                // Clear any prior timer defensively.
                if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
                reconnectTimer.current = setTimeout(() => {
                    // Only reconnect if we're still the current epoch AND
                    // the hook hasn't been torn down.
                    if (epoch === epochRef.current) connect();
                }, delay);
            };
            ws.onerror = () => {
                if (epoch !== epochRef.current) return;
                debugLog("ws", `onerror epoch=${epoch}`);
                try { ws.close(); } catch (_) { /* already closed */ }
            };
        } catch (_) {
            setStatus("disconnected");
        }
    }, [enabled, sendJson]);

    // Ping + presence refresh loop.
    useEffect(() => {
        if (!enabled) return undefined;
        // Bump the epoch so any handlers left over from the previous mount
        // (StrictMode double-invocation) refuse to touch state / schedule
        // reconnects — see `epoch !== epochRef.current` guards above.
        epochRef.current += 1;
        const myEpoch = epochRef.current;
        debugLog("ws", `mount epoch=${myEpoch}`);
        connect();
        const ping = setInterval(() => {
            sendJson({ type: "ping" });
            sendJson({ type: "presence", state: isTabActive() ? "active" : "idle" });
        }, 30000);

        // Presence transitions — visibility and focus/blur events.
        const emitPresence = () => sendJson({ type: "presence", state: isTabActive() ? "active" : "idle" });
        document.addEventListener("visibilitychange", emitPresence);
        window.addEventListener("focus", emitPresence);
        window.addEventListener("blur", emitPresence);

        teardownRef.current = () => {
            debugLog("ws", `teardown epoch=${myEpoch}`);
            clearInterval(ping);
            document.removeEventListener("visibilitychange", emitPresence);
            window.removeEventListener("focus", emitPresence);
            window.removeEventListener("blur", emitPresence);
            if (reconnectTimer.current) {
                clearTimeout(reconnectTimer.current);
                reconnectTimer.current = null;
            }
            // Say goodbye so the backend clears presence immediately.
            sendJson({ type: "presence", state: "idle" });
            try { wsRef.current?.close(); } catch (_) { /* already closed */ }
            wsRef.current = null;
        };
        return teardownRef.current;
    }, [connect, enabled, sendJson]);

    return { status };
}
